import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Notify } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaPen } from 'react-icons/fa'
import api from '../../../../helpers/apiRequester'
import { dialCodes } from '../../../../data/dialCodes'
import { CollapsibleSection } from '../../omnibusShared/collapsibleSection'
import styles from '../shared/mhlShared.module.scss'
import type { MhlClient } from '../shared/mhlTypes'
import { formatDate, formatDateTime, fullName } from '../shared/mhlFormatting'

const CLIENT_TITLES = ['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof', 'Sir', 'Dame', 'Rev', 'Capt', 'Major', 'Lt', 'Col', 'General', 'Other']

const countryName = (code?: string | null) =>
    code ? dialCodes.find((d) => d.iso2 === code.toUpperCase())?.name || code : null

const addressLines = (c: MhlClient) =>
    [
        c.address1,
        c.address2,
        c.address3,
        c.address4,
        c.city && c.city !== c.address3 ? c.city : null,
        c.postcode,
        countryName(c.country),
    ].filter(Boolean)

const dateInputValue = (value: string | null | undefined) => {
    if (!value) return ''
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
        const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/)
        return match?.[1] || ''
    }
    const year = parsed.getFullYear()
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const day = String(parsed.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

type Draft = {
    title: string
    firstname: string
    middleNames: string
    lastname: string
    dob: string
    nationalInsurance: string
    email: string
    telephone: string
    homeTelephone: string
    address1: string
    address2: string
    address3: string
    address4: string
    city: string
    postcode: string
    country: string
}

const draftFromClient = (client: MhlClient): Draft => ({
    title: client.title || '',
    firstname: client.firstname || '',
    middleNames: client.middle_names || '',
    lastname: client.lastname || '',
    dob: dateInputValue(client.dob),
    nationalInsurance: (client.national_insurance || '').replace(/\s+/g, '').toUpperCase(),
    email: client.email || '',
    telephone: client.mobile_number || client.telephone || '',
    homeTelephone: client.home_telephone || '',
    address1: client.address1 || '',
    address2: client.address2 || '',
    address3: client.address3 || '',
    address4: client.address4 || '',
    city: client.city || '',
    postcode: client.postcode || '',
    country: client.country || '',
})

const Field = ({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) => (
    <label className={styles.editField}>
        <span>
            {label}
            {required && <span className={styles.req}>*</span>}
        </span>
        {children}
    </label>
)

export const ClientDetailsSection = ({
    client,
    sourceLabel,
    cfaSigned,
    onSaved,
}: {
    client: MhlClient
    sourceLabel: string | null
    cfaSigned: boolean
    onSaved: () => Promise<void> | void
}) => {
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draft, setDraft] = useState<Draft>(() => draftFromClient(client))

    useEffect(() => {
        setEditing(false)
        setDraft(draftFromClient(client))
    }, [client])

    const setField = (key: keyof Draft, value: string) => setDraft((current) => ({ ...current, [key]: value }))

    const ukAddress = !draft.country || draft.country === 'GB'
    const titleOptions =
        draft.title && draft.title.length <= 7 && !CLIENT_TITLES.includes(draft.title) ? [draft.title, ...CLIENT_TITLES] : CLIENT_TITLES
    const knownCountry = !draft.country || dialCodes.some((d) => d.iso2 === draft.country)
    const countries = knownCountry ? dialCodes : [{ iso2: draft.country, name: draft.country, code: '' }, ...dialCodes]

    const save = async (event: FormEvent) => {
        event.preventDefault()
        setSaving(true)
        try {
            const res = await api({
                url: '/military-hearing-loss-client-update',
                data: {
                    clientId: client.id,
                    title: draft.title,
                    firstname: draft.firstname,
                    middleNames: draft.middleNames,
                    lastname: draft.lastname,
                    dob: draft.dob,
                    nationalInsurance: draft.nationalInsurance,
                    email: draft.email,
                    telephone: draft.telephone,
                    homeTelephone: draft.homeTelephone,
                    address1: draft.address1,
                    address2: draft.address2,
                    address3: draft.address3,
                    address4: draft.address4,
                    city: draft.city,
                    postcode: draft.postcode,
                    country: draft.country || null,
                },
            })
            if (!res.data?.success) {
                Report.failure('Update failed', res.data?.message || 'Could not save the client details', 'Okay')
                return
            }
            setEditing(false)
            Notify.success('Client details saved')
            await onSaved()
        } catch (err: any) {
            Report.failure('Update failed', err?.response?.data?.message || err?.message || 'Could not save the client details', 'Okay')
        } finally {
            setSaving(false)
        }
    }

    const name = fullName([client.title, client.firstname, client.middle_names, client.lastname])

    return (
        <CollapsibleSection
            title="Client details"
            defaultOpen
            forceOpen={editing}
            actions={
                editing ? (
                    <>
                        <button type="button" className={styles.btn} onClick={() => setEditing(false)} disabled={saving}>
                            Cancel
                        </button>
                        <button type="submit" form="mhl-client-details" className={`${styles.btn} ${styles.btnPrimary}`} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </>
                ) : (
                    <button type="button" className={styles.btn} onClick={() => { setDraft(draftFromClient(client)); setEditing(true) }}>
                        <FaPen size={11} /> Edit
                    </button>
                )
            }
        >
            {editing ? (
                <form id="mhl-client-details" onSubmit={(event) => void save(event)}>
                    {cfaSigned && (
                        <p className={styles.hint} style={{ marginBottom: 12 }}>
                            A CFA has been signed. Changing these details does not update the signed documents.
                        </p>
                    )}
                    <div className={styles.subTitle}>Personal</div>
                    <div className={styles.editGrid}>
                        <Field label="Title" required>
                            <select value={draft.title} onChange={(e) => setField('title', e.target.value)}>
                                <option value="">Select…</option>
                                {titleOptions.map((title) => (
                                    <option key={title} value={title}>
                                        {title}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="First name" required>
                            <input value={draft.firstname} onChange={(e) => setField('firstname', e.target.value)} />
                        </Field>
                        <Field label="Middle names">
                            <input value={draft.middleNames} onChange={(e) => setField('middleNames', e.target.value)} />
                        </Field>
                        <Field label="Surname" required>
                            <input value={draft.lastname} onChange={(e) => setField('lastname', e.target.value)} />
                        </Field>
                        <Field label="Date of birth" required>
                            <input type="date" value={draft.dob} onChange={(e) => setField('dob', e.target.value)} />
                        </Field>
                        <Field label="NI number" required>
                            <input
                                value={draft.nationalInsurance}
                                onChange={(e) => setField('nationalInsurance', e.target.value.toUpperCase().replace(/\s+/g, ''))}
                                placeholder="AB123456C"
                            />
                        </Field>
                    </div>

                    <div className={styles.subTitle}>Contact</div>
                    <div className={styles.editGrid}>
                        <Field label="Email" required>
                            <input type="email" value={draft.email} onChange={(e) => setField('email', e.target.value)} />
                        </Field>
                        <Field label="Mobile" required>
                            <input type="tel" value={draft.telephone} onChange={(e) => setField('telephone', e.target.value)} />
                        </Field>
                        <Field label="Home phone">
                            <input type="tel" value={draft.homeTelephone} onChange={(e) => setField('homeTelephone', e.target.value)} />
                        </Field>
                    </div>

                    <div className={styles.subTitle}>Address</div>
                    <div className={styles.editGrid}>
                        <Field label="Address line 1" required>
                            <input value={draft.address1} onChange={(e) => setField('address1', e.target.value)} />
                        </Field>
                        <Field label="Address line 2">
                            <input value={draft.address2} onChange={(e) => setField('address2', e.target.value)} />
                        </Field>
                        <Field label="Address line 3">
                            <input value={draft.address3} onChange={(e) => setField('address3', e.target.value)} />
                        </Field>
                        <Field label="Address line 4">
                            <input value={draft.address4} onChange={(e) => setField('address4', e.target.value)} />
                        </Field>
                        <Field label="City" required>
                            <input value={draft.city} onChange={(e) => setField('city', e.target.value)} />
                        </Field>
                        <Field label={ukAddress ? 'Postcode' : 'Postcode / ZIP code'} required={ukAddress}>
                            <input value={draft.postcode} onChange={(e) => setField('postcode', e.target.value.toUpperCase())} />
                        </Field>
                        <Field label="Country">
                            <select value={draft.country} onChange={(e) => setField('country', e.target.value)}>
                                <option value="">Not set</option>
                                {countries.map((country) => (
                                    <option key={country.iso2} value={country.iso2}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <div className={styles.subTitle}>Record</div>
                    <dl className={styles.defList}>
                        <dt>Client created</dt>
                        <dd>{formatDateTime(client.created_at)}</dd>
                        {sourceLabel && (
                            <>
                                <dt>Source</dt>
                                <dd>{sourceLabel}</dd>
                            </>
                        )}
                    </dl>
                </form>
            ) : (
                <div className={styles.columns}>
                    <div>
                        <div className={styles.subTitle}>Personal</div>
                        <dl className={styles.defList}>
                            <dt>Full name</dt>
                            <dd>{name}</dd>
                            <dt>Date of birth</dt>
                            <dd>{formatDate(client.dob)}</dd>
                            <dt>NI number</dt>
                            <dd>{client.national_insurance || '—'}</dd>
                        </dl>
                        <div className={styles.subTitle}>Contact</div>
                        <dl className={styles.defList}>
                            <dt>Email</dt>
                            <dd>{client.email || '—'}</dd>
                            <dt>Mobile</dt>
                            <dd>{client.mobile_number || client.telephone || '—'}</dd>
                            <dt>Home phone</dt>
                            <dd>{client.home_telephone || '—'}</dd>
                        </dl>
                    </div>
                    <div>
                        <div className={styles.subTitle}>Address</div>
                        <dl className={styles.defList}>
                            <dt>Address</dt>
                            <dd>
                                {addressLines(client).length ? addressLines(client).map((line, i) => <div key={i}>{line}</div>) : '—'}
                            </dd>
                        </dl>
                        <div className={styles.subTitle}>Record</div>
                        <dl className={styles.defList}>
                            <dt>Client created</dt>
                            <dd>{formatDateTime(client.created_at)}</dd>
                            {sourceLabel && (
                                <>
                                    <dt>Source</dt>
                                    <dd>{sourceLabel}</dd>
                                </>
                            )}
                        </dl>
                    </div>
                </div>
            )}
        </CollapsibleSection>
    )
}
